import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import Alert from '../../styleguide/Alert'
import IconButton from '../../styleguide/buttons/IconButton'
import CloseIcon from '../../styleguide/icons/CloseIcon'
import Snackbar from '../../styleguide/Snackbar'
import errorsActions from '../errors/actions'
import { selectMissingAuthErrors } from '../errors/selectors'
import notificationsActions from './actions'
import { selectNotifications, selectNotification } from './selectors'
import appActions from '../app/actions'
import { selectOnline } from '../app/selectors'
import Notification from './Notification'
import useNotification from '../../hooks/useNotifications'
import { useTeloDispatch, useTeloSelector } from '../../store'
import { useNotificationErrors } from '../../hooks/useNotificationErrors'

const useNavigatorOnLine = () => {
	const dispatch = useTeloDispatch()

	useEffect(() => {
		const setOnline = () => dispatch(appActions.setOnline(true))
		const setOffline = () => dispatch(appActions.setOnline(false))

		window.addEventListener('online', setOnline)
		window.addEventListener('offline', setOffline)

		return () => {
			window.removeEventListener('online', setOnline)
			window.removeEventListener('offline', setOffline)
		}
	}, [dispatch])
}

const NotificationArea = () => {
	const { t } = useTranslation()

	const inputRef = React.useRef<HTMLDivElement>(null)

	const notifications = useTeloSelector(selectNotifications)
	const missingAuthErrors = useTeloSelector(selectMissingAuthErrors)
	const online = useTeloSelector(selectOnline)
	const offlineNotification = useTeloSelector(
		selectNotification('offline-notification'),
	)
	const dispatch = useTeloDispatch()

	useNotification()

	useNotificationErrors()

	useNavigatorOnLine()

	useEffect(() => {
		!online &&
			offlineNotification === undefined &&
			dispatch(
				notificationsActions.addNotification({
					id: 'offline-notification',
					type: 'error',
					message: 'errors.connection',
					autoClose: false,
					messageIsLabelKey: true,
				}),
			)
		online &&
			offlineNotification !== undefined &&
			dispatch(notificationsActions.removeNotification('offline-notification'))
	}, [dispatch, online, offlineNotification])

	useEffect(() => {
		// Try to prevent the issue: play() failed because the user didn't interact with the document first
		// Simulate a click on a empty div
		if (inputRef.current !== null) {
			inputRef.current.click()
		}
	}, [])

	return (
		<>
			<div ref={inputRef} onClick={() => {}} />
			{notifications.map(notification => (
				<Notification
					onClose={() =>
						dispatch(notificationsActions.removeNotification(notification._id))
					}
					key={notification._id}
					notification={notification}
				/>
			))}
			{missingAuthErrors.map(({ route, id }) => (
				<Snackbar
					key={id}
					open={true}
					action={
						<React.Fragment>
							<IconButton
								size="small"
								aria-label="close"
								color="inherit"
								onClick={() =>
									dispatch(errorsActions.removeMissingAuthError(id))
								}
							>
								<CloseIcon />
							</IconButton>
						</React.Fragment>
					}
				>
					<Alert severity="error">{`${t(
						'errors.missingAuthError',
					)} ${route}`}</Alert>
				</Snackbar>
			))}
		</>
	)
}

export default NotificationArea
